import {
  nonEditable,
  textCell,
  headerCell
} from "../cells";
import { HEADER_ROW_ID, ROW_HEIGHT } from "../constants";
import { getGroupRows } from "../tenant-statement/getTenantStatementRows";
import { formatCurrency, formatDate } from "@/utils/helpers";

// The supplier statement check: a short reconciliation, then every exception with the document it is about and
// what is wrong with it. S rows are the reconciliation (documents balance, less money not applied, what the ledger
// should hold, what it holds, the difference); E rows are the exceptions behind the difference.
const EXCEPTION_GROUPS = [
  { code: "E1", title: "Not applied to an invoice" },
  { code: "E4", title: "Applied for more than the total" },
  { code: "E2", title: "Applied outside the statement" },
  { code: "E3", title: "Ledger and documents differ" },
];

// The rows that carry the reconciliation's answer are set in bold so the eye finds them first.
const KEY_SUMMARY_CODES = new Set(["S1", "S7", "S8", "S9"]);

// The report sends a placeholder date on summary rows, which have no date of their own.
const formatDateCell = (value) => (value && !String(value).startsWith("1970") ? formatDate(new Date(value)) : "");

export function getSupplierCheckColumns() {
  return [
    {
      label: "Check", value: true, columnId: "code", width: 150, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Item", value: true, columnId: "documentType", width: 420, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Document No", value: true, columnId: "documentNumber", width: 150, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Date", value: true, columnId: "documentDate", width: 100, resizable: true, formatter: formatDateCell
    },
    {
      label: "Amount", value: true, summable: true, columnId: "amount", width: 140, resizable: true, formatter: (value) => formatCurrency(String(value ?? 0))
    },
    {
      label: "Detail", value: true, columnId: "related", width: 330, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "What is wrong", value: true, columnId: "note", width: 560, resizable: true, formatter: (value) => String(value || "")
    },
  ];
}

function getHeaderRow(columns) {
  return {
    rowId: HEADER_ROW_ID,
    height: ROW_HEIGHT,
    cells: columns.map((column) => headerCell(column.label)),
  };
}

function toRow(item, columns, className = "") {
  return {
    rowId: item.id,
    height: ROW_HEIGHT,
    cells: columns.map((column) => nonEditable(
      textCell({ text: column.formatter ? column.formatter(item[column.columnId]) : item[column.columnId] ?? "" }),
      className
    )),
  };
}

export function getSupplierCheckRows({ data, columns }) {
  if (!data?.data?.length) {
    return [];
  }

  const items = data.data.map((item, index) => ({
    ...item,
    id: `${item.code}-${item.documentNumber || "summary"}-${index}`,
    amount: Number(item.amount) || 0,
  }));

  const summary = items.filter((item) => item.kind === "Summary");
  const exceptions = items.filter((item) => item.kind === "Exception");
  const difference = summary.find((item) => item.code === "S9")?.amount ?? 0;

  const resultRow = {
    rowId: "supplier-check-result",
    height: ROW_HEIGHT,
    cells: columns.map((column, index) => nonEditable(
      textCell({
        text: index === 1 ?
          "No problems found: the ledger agrees with the documents." :
          ""
      }),
      "font-bold"
    )),
  };

  return [
    getHeaderRow(columns),
    ...summary.map((item) => toRow(item, columns, KEY_SUMMARY_CODES.has(item.code) ? "font-bold" : "")),
    ...(exceptions.length === 0 && Math.abs(difference) < 0.005 ? [resultRow] : []),
    ...EXCEPTION_GROUPS.flatMap((group) => getGroupRows(
      group.title,
      exceptions.filter((item) => item.code === group.code),
      columns,
      formatCurrency
    )),
  ];
}
